/**
 * Card-terminal orders this booth opened and has not seen the end of.
 *
 * Session state lives only in the zustand store, so a renderer reload, a crash,
 * a power cut or a reset forgets the order the booth pushed to its Mercado Pago
 * Point terminal. The terminal does not forget: it keeps showing the amount for
 * as long as Mercado Pago keeps the order alive (the booth's payment window plus
 * two minutes, `POINT_ORDER_EXPIRY_MARGIN_MS`), and whoever taps a card on it
 * pays for a session nobody is watching any more.
 *
 * So the payment page also writes each open order here and drops it once it has
 * seen how the order ended. Anything left over belongs to a session the booth is
 * no longer showing, and `sweepOpenTerminalOrders` cancels it:
 *
 * - `cancelled` / `skipped`: gone, or already dead. Forgotten.
 * - `processed`: somebody paid it after all. Studio is asked for its status once,
 *   which records the earning against the Session row, and it is reported so an
 *   operator can refund or serve the guest. Forgotten.
 * - `at_terminal` / `unknown`: a guest is paying right now, or Studio could not be
 *   reached. Kept and asked again on the next sweep.
 *
 * localStorage, not the sessionStorage the paid-session checkpoint uses: an
 * operator restarting the app, or a power cut, is exactly when an order is left
 * on the terminal.
 */
import type { PointCancelOutcome } from '@renderer/utils/pointPayment'

export const OPEN_TERMINAL_ORDERS_KEY = 'dreambooth.openTerminalOrders.v1'

/**
 * How much longer the Mercado Pago order lives than the booth's payment window.
 * Mirrors Studio's `POINT_ORDER_EXPIRY_MARGIN_SECONDS`
 * (`helpers/mercadopagoPointHelper.ts`).
 */
export const POINT_ORDER_EXPIRY_MARGIN_MS = 120 * 1000

/**
 * Past its expiry an order can no longer be paid, but one that WAS paid still
 * needs its earning recorded, so it is asked about for this long before it is
 * forgotten unanswered. A booth that stays offline longer than this leaves that
 * earning to the Order webhook.
 */
const GIVE_UP_AFTER_EXPIRY_MS = 60 * 60 * 1000

/** One booth drives one terminal; a handful of entries is already abnormal. */
const MAX_ENTRIES = 10

export interface OpenTerminalOrder {
  /** Our order id (`mppoint_…`). */
  orderId: string
  /** The Mercado Pago order id, echoed as `?ref=` on cancel and status calls. */
  reference: string | null
  /** For the log line only. */
  sessionId?: string
  /** Epoch ms after which Mercado Pago has expired the order on its own. */
  expiresAt: number
}

export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function defaultStore(): KeyValueStore | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null
  } catch {
    return null
  }
}

function isEntry(value: unknown): value is OpenTerminalOrder {
  if (!value || typeof value !== 'object') return false
  const e = value as Partial<OpenTerminalOrder>
  return (
    typeof e.orderId === 'string' &&
    e.orderId !== '' &&
    typeof e.expiresAt === 'number' &&
    Number.isFinite(e.expiresAt) &&
    (e.reference === null || typeof e.reference === 'string')
  )
}

export function readOpenTerminalOrders(store = defaultStore()): OpenTerminalOrder[] {
  if (!store) return []
  try {
    const raw = store.getItem(OPEN_TERMINAL_ORDERS_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(isEntry) : []
  } catch {
    return []
  }
}

function write(entries: OpenTerminalOrder[], store: KeyValueStore | null): void {
  if (!store) return
  try {
    if (entries.length) store.setItem(OPEN_TERMINAL_ORDERS_KEY, JSON.stringify(entries))
    else store.removeItem(OPEN_TERMINAL_ORDERS_KEY)
  } catch {
    /* quota or privacy mode: the order still expires at Mercado Pago on its own */
  }
}

export function rememberOpenTerminalOrder(order: OpenTerminalOrder, store = defaultStore()): void {
  const others = readOpenTerminalOrders(store).filter((e) => e.orderId !== order.orderId)
  write([...others, order].slice(-MAX_ENTRIES), store)
}

export function forgetOpenTerminalOrder(
  orderId: string | null | undefined,
  store = defaultStore()
): void {
  if (!orderId) return
  const entries = readOpenTerminalOrders(store)
  const kept = entries.filter((e) => e.orderId !== orderId)
  if (kept.length !== entries.length) write(kept, store)
}

export interface SweepDeps {
  /**
   * The order the payment page is showing right now, with its status in the
   * session store. A pending one is left alone; a settled one is forgotten.
   */
  live?: { orderId?: string | null; status?: string | null } | null
  cancel: (orderId: string, reference: string | null) => Promise<PointCancelOutcome>
  /** Ask Studio for the order's status, which settles the earning when it is paid. */
  recordPaid: (orderId: string, reference: string | null) => Promise<void>
  report: (message: string, details: Record<string, unknown>) => void
  store?: KeyValueStore | null
  now?: number
}

/**
 * Cancel the leftover orders. Returns how many are still kept, so a caller can
 * tell whether another pass is worth scheduling.
 */
export async function sweepOpenTerminalOrders(deps: SweepDeps): Promise<{ remaining: number }> {
  const store = deps.store === undefined ? defaultStore() : deps.store
  const now = deps.now ?? Date.now()
  let remaining = 0

  for (const entry of readOpenTerminalOrders(store)) {
    if (deps.live?.orderId && deps.live.orderId === entry.orderId) {
      if (deps.live.status === 'settlement') forgetOpenTerminalOrder(entry.orderId, store)
      continue
    }
    if (now > entry.expiresAt + GIVE_UP_AFTER_EXPIRY_MS) {
      forgetOpenTerminalOrder(entry.orderId, store)
      deps.report('Card terminal order forgotten without an answer', {
        orderId: entry.orderId,
        sessionId: entry.sessionId
      })
      continue
    }

    const outcome = await deps.cancel(entry.orderId, entry.reference)
    if (outcome === 'cancelled' || outcome === 'skipped') {
      forgetOpenTerminalOrder(entry.orderId, store)
      // `skipped`: already declined, cancelled or expired. Nothing to say.
      if (outcome === 'cancelled') {
        deps.report('Cancelled a card terminal order left open by an earlier session', {
          orderId: entry.orderId,
          sessionId: entry.sessionId
        })
      }
      continue
    }
    if (outcome === 'processed') {
      try {
        await deps.recordPaid(entry.orderId, entry.reference)
      } catch {
        // Studio's session upload check and the Order webhook still settle it.
      }
      forgetOpenTerminalOrder(entry.orderId, store)
      deps.report('A card terminal order was paid after its session ended. Refund or serve the guest.', {
        orderId: entry.orderId,
        sessionId: entry.sessionId,
        level: 'error'
      })
      continue
    }
    remaining += 1
  }
  return { remaining }
}

let sweepInFlight: Promise<{ remaining: number }> | null = null

/**
 * The sweep, wired to Studio and the session store. Safe to call often: it is a
 * localStorage read when nothing is open, and concurrent calls share one pass.
 */
export function sweepLeftoverTerminalOrders(): Promise<{ remaining: number }> {
  if (!readOpenTerminalOrders().length) return Promise.resolve({ remaining: 0 })
  if (sweepInFlight) return sweepInFlight
  sweepInFlight = (async (): Promise<{ remaining: number }> => {
    // Lazy: keeps this module light for the boot path and unit tests.
    const [{ default: MidtransUtils }, { useSessionStateStore }] = await Promise.all([
      import('@renderer/utils/midtransUtils'),
      import('@renderer/stores/sessionState')
    ])
    const live = useSessionStateStore.getState().transaction
    return sweepOpenTerminalOrders({
      live: { orderId: live?.orderId, status: live?.status },
      cancel: (orderId, reference) => MidtransUtils.cancelPayment(orderId, { reference }),
      recordPaid: async (orderId, reference) => {
        await MidtransUtils.checkPaymentStatus(orderId, { reference })
      },
      report: (message, details) => {
        console.warn(`[CARD TERMINAL] ${message}`, details)
        // The activity log only exists inside a session. Called from a timer,
        // never from a store subscription (see gracefulQuit.ts for why).
        const session = useSessionStateStore.getState()
        if (!session.id) return
        try {
          session.logActivity(message, details)
        } catch {
          // A diagnostic line must never take the app down with it.
        }
      }
    })
  })().finally(() => {
    sweepInFlight = null
  })
  return sweepInFlight
}
